import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { TopBar } from '../components/TopBar';
import { LeftRail } from '../components/LeftRail';
import { ScreenShell } from '../components/ScreenShell';
import { Tabs, type TabDef } from '../components/Tabs';
import { VerdictHeader } from '../components/VerdictHeader';
import { KpiTile } from '../components/KpiTile';
import { SignalCard } from '../components/SignalCard';
import { AnalyticalBriefing } from '../components/AnalyticalBriefing';
import { DimensionGauge } from '../components/DimensionGauge';
import { SentimentRadar } from '../components/SentimentRadar';
import { EntityTable } from '../components/EntityTable';
import { SectionTimeline } from '../components/SectionTimeline';
import { QnaTable } from '../components/QnaTable';
import { NumericalTable } from '../components/NumericalTable';
import { LinguisticIndicatorTile } from '../components/LinguisticIndicatorTile';
import { LinguisticFingerprint } from '../components/LinguisticFingerprint';
import { SectorLexiconAccordion } from '../components/SectorLexiconAccordion';
import { ExportMenu, type ExportFormat } from '../components/ExportMenu';
import { ShortcutsModal } from '../components/ShortcutsModal';
import { InfoPopover } from '../components/InfoPopover';
import { RiskQuotes } from '../components/RiskQuotes';
import { VerificationList } from '../components/VerificationList';
import { SpeakerSentimentPanel } from '../components/SpeakerSentimentPanel';
import { CohortPercentilePanel } from '../components/CohortPercentilePanel';
import { PriorQuarterDiffPanel } from '../components/PriorQuarterDiffPanel';
import { TailRiskMeter } from '../components/TailRiskMeter';
import { GuidanceBridge } from '../components/GuidanceBridge';
import { useTheme } from '../hooks/useTheme';
import { useHistory } from '../hooks/useHistory';
import { readStashedResult } from '../hooks/useResultStore';
import { findResultBySlug, mockEarningsCall } from '../data';
import { fetchResultBySlug } from '../lib/api';
import { toMarkdown, toSummary, toNumericalCsv, toDimensionsCsv } from '../lib/exports';
import type { AnalysisResult } from '../lib/types';
import { qualitativeTier } from '../lib/verdict';
import { formatPeriod } from '../lib/period';
import { SECTOR_LABELS } from '../lib/types';
import styles from './ResultScreen.module.css';

type TabId = 'verdict' | 'evidence' | 'language' | 'context';

const documentTypeLabel: Record<string, string> = {
  'earnings-call': 'Earnings Call',
  '10-k-mdna': '10-K MD&A',
  'credit-report': 'Credit Memo',
  'press-release': 'Press Release',
  other: 'Document',
  auto: 'Document',
};

// Tooltip copy for each dimension. Looked up by label so renames in the
// data don't break the join (just add the new label here).
const DIMENSION_TOOLTIPS: Record<string, string> = {
  'Tone & Confidence':
    'How declarative the language is. Confident speakers use unhedged statements ("we will", "we are"); cautious ones stack qualifiers. Higher = more direct, certain delivery.',
  'Forward-Looking Posture':
    'Density of forward-looking modal verbs ("expect", "anticipate", "see") relative to backward-looking commentary. Higher = more guidance on the future.',
  'Directness':
    'Inverse of hedging density — fewer "if / could / may / perhaps" qualifiers means more directness. Higher = clearer commitments, less weasel words.',
  'Hedging & Qualifiers':
    'Density of hedging language. Lower scores indicate more direct delivery; higher scores stack qualifiers. (Inverse of Directness.)',
  'Q&A Discipline':
    'How consistently analyst questions are answered directly versus deflected. Counts non-answers, deflections and hedged replies in the Q&A. Higher = analysts got real answers.',
  'Numerical Density':
    'Frequency of quantified claims per 1k tokens. Higher = a quantitatively-grounded read; lower = vague narrative.',
  'Quantification':
    'Frequency of quantified claims per 1k tokens. Higher = a quantitatively-grounded read; lower = vague narrative.',
  'Risk Disclosure':
    'Volume and specificity of risk language. High in 10-K Risk Factors and credit memos; lower in earnings calls.',
};

function isTabId(s: string | null): s is TabId {
  return s === 'verdict' || s === 'evidence' || s === 'language' || s === 'context';
}

export function ResultScreen() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { theme, toggle } = useTheme();
  const { items: history, push } = useHistory();
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [printMode, setPrintMode] = useState(false);

  const initialTab: TabId = (() => {
    const fromUrl = searchParams.get('tab');
    return isTabId(fromUrl) ? fromUrl : 'verdict';
  })();
  const [activeTab, setActiveTabState] = useState<TabId>(initialTab);

  const [result, setResult] = useState<AnalysisResult>(() => {
    const fromMocks = findResultBySlug(slug ?? '');
    if (fromMocks) return fromMocks;
    const stashed = slug ? readStashedResult(slug) : undefined;
    if (stashed) return stashed;
    return mockEarningsCall;
  });

  // Hydrate from the permalink endpoint when the slug isn't a mock.
  useEffect(() => {
    let cancelled = false;
    const isMock = slug && findResultBySlug(slug);
    if (isMock || !slug) return;
    void fetchResultBySlug(slug).then((r) => {
      if (cancelled) return;
      if (r) setResult(r);
    }).catch(() => { /* non-fatal — sessionStorage / mock fallback already in state */ });
    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    push(result);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result.slug]);

  // Print flow: when the user picks "Save as PDF" we flip into print mode
  // (renders ALL tab panels stacked so the printed output is the full report,
  // not just the active tab) and then trigger the browser's print dialog.
  // Restores after the dialog closes (or returns).
  useEffect(() => {
    if (!printMode) return;
    document.documentElement.dataset.printMode = 'true';
    // Two RAFs to give layout a chance to settle before invoking print.
    let cancelled = false;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (cancelled) return;
        try { window.print(); } catch { /* user denied or no print available */ }
        // After print() returns, reset.
        setPrintMode(false);
      });
    });
    return () => {
      cancelled = true;
      delete document.documentElement.dataset.printMode;
    };
  }, [printMode]);

  // Esc / theme toggle keyboard shortcuts.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.key === 't' || e.key === 'T') && !(e.target as HTMLElement)?.closest?.('input, textarea')) {
        toggle();
      }
      if (e.key === '?' && !(e.target as HTMLElement)?.closest?.('input, textarea')) {
        e.preventDefault();
        setShortcutsOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [toggle]);

  const setActiveTab = (id: TabId) => {
    setActiveTabState(id);
    const next = new URLSearchParams(searchParams);
    if (id === 'verdict') next.delete('tab');
    else next.set('tab', id);
    setSearchParams(next, { replace: true });
    // Scroll the main content to the top so each tab feels like a fresh view.
    document.getElementById('main')?.scrollTo({ top: 0, behavior: 'auto' });
    window.scrollTo({ top: 0, behavior: 'auto' });
  };

  const onExport = async (format: ExportFormat) => {
    if (format === 'pdf') {
      // No client-side PDF library — render all tab panels stacked + trigger
      // the browser's print dialog. User picks "Save as PDF" → real PDF.
      setPrintMode(true);
      return;
    }
    if (format === 'summary') {
      await navigator.clipboard.writeText(toSummary(result)).catch(() => {});
      return;
    }
    if (format === 'json') {
      await navigator.clipboard.writeText(JSON.stringify(result, null, 2)).catch(() => {});
      return;
    }
    // The remaining formats produce downloadable files.
    const ext: Record<Exclude<ExportFormat, 'pdf' | 'summary' | 'json'>, string> = {
      md: 'md', csv: 'csv', 'dimensions-csv': 'csv',
    };
    const mime: Record<Exclude<ExportFormat, 'pdf' | 'summary' | 'json'>, string> = {
      md: 'text/markdown',
      csv: 'text/csv',
      'dimensions-csv': 'text/csv',
    };
    const filename: Record<Exclude<ExportFormat, 'pdf' | 'summary' | 'json'>, string> = {
      md: `${result.slug}.md`,
      csv: `${result.slug}-numerical.csv`,
      'dimensions-csv': `${result.slug}-dimensions.csv`,
    };
    const text =
      format === 'md' ? toMarkdown(result)
      : format === 'csv' ? toNumericalCsv(result)
      : format === 'dimensions-csv' ? toDimensionsCsv(result)
      : '';
    const blob = new Blob([text], { type: mime[format] });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename[format] ?? `${result.slug}.${ext[format]}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const onShare = async () => {
    const url = `${window.location.origin}/result/${result.slug}`;
    await navigator.clipboard.writeText(url).catch(() => {});
  };

  const onViewSource = () => navigate(`/result/${result.slug}?source=1`);

  const exportMenu = <ExportMenu onExport={onExport} onShare={onShare} onViewSource={onViewSource} />;

  // Build tab list — only show tabs that have content. Verdict is always present.
  const tabs: TabDef<TabId>[] = [
    { id: 'verdict', label: 'Verdict' },
  ];
  const hasEvidence =
    result.entities.length > 0 ||
    result.sections.length > 0 ||
    result.qna.length > 0 ||
    result.numerical.length > 0;
  if (hasEvidence) tabs.push({ id: 'evidence', label: 'Evidence' });
  const hasLanguage =
    result.linguisticIndicators.length > 0 ||
    result.fingerprint.length > 0 ||
    result.sectorLexicon.length > 0 ||
    !!result.tailRisk;
  if (hasLanguage) tabs.push({ id: 'language', label: 'Language' });
  const hasContext =
    !!result.priorQuarter ||
    !!result.cohort ||
    (result.speakers && result.speakers.length > 0) ||
    result.riskQuotes.length > 0 ||
    result.verification.length > 0 ||
    !!result.summary ||
    result.advanced.length > 0;
  if (hasContext) tabs.push({ id: 'context', label: 'Context' });

  // If the user has a tab in the URL that the document doesn't support, fall
  // back to the verdict tab silently.
  const safeActiveTab: TabId = tabs.some((t) => t.id === activeTab) ? activeTab : 'verdict';

  return (
    <ScreenShell
      topBar={
        <TopBar
          historyCount={history.length}
          theme={theme}
          onShortcuts={() => setShortcutsOpen(true)}
          onHistory={() => navigate('/history')}
          onToggleTheme={toggle}
        />
      }
      rail={
        <LeftRail
          history={history}
          onViewSource={onViewSource}
        />
      }
    >
      <Tabs<TabId>
        tabs={tabs}
        active={safeActiveTab}
        onChange={setActiveTab}
        ariaLabel="Result sections"
      />

      {(printMode || safeActiveTab === 'verdict') && (
        <div role="tabpanel" id="tabpanel-verdict" aria-labelledby="tab-verdict" className={[styles.tabPanel, printMode ? styles.printPanel : ''].filter(Boolean).join(' ')}>
          {printMode && <h2 className={styles.printTabHeading}>Verdict</h2>}
          <section id="overview" className={styles.section}>
            <VerdictHeader
              verdict={result.verdict}
              score={result.score}
              confidence={result.confidence}
              summary={result.summary}
              title={result.title}
              headline={result.headline}
              direction="C"
              documentLabel={[
                documentTypeLabel[result.documentType] ?? 'Document',
                result.ticker,
                formatPeriod(result.period),
                result.sector ? SECTOR_LABELS[result.sector] : undefined,
              ].filter(Boolean).join(' · ')}
              exportMenu={exportMenu}
            />
            <div className={styles.kpiGrid}>
              <KpiTile
                label="Confidence"
                value={`${result.kpi.confidence}%`}
                qualifier={qualitativeTier(result.kpi.confidence / 100)}
                tone="positive"
                tooltip={
                  <span>
                    How sure the analyser is in its verdict. Driven by signal density, Q&amp;A discipline, alignment of numerical claims with their qualitative framing, and the consistency of the document's overall trajectory. Above 80% = strong, dense signal; below 60% = sparse text or contradictory cues.
                  </span>
                }
              />
              <KpiTile
                label="Red Flags"
                value={result.kpi.redFlags}
                qualifier={result.kpi.redFlags > 5 ? 'ELEVATED' : result.kpi.redFlags > 2 ? 'MODERATE' : 'LOW'}
                tone={result.kpi.redFlags > 5 ? 'negative' : result.kpi.redFlags > 2 ? 'warning' : 'positive'}
                tooltip={
                  <span>
                    Count of items the analyser flagged for review: non-answers and deflections in the Q&amp;A, plus failed verification checks. <strong>Low</strong> = clean delivery; <strong>elevated</strong> = something worth investigating before forming a view.
                  </span>
                }
              />
              <KpiTile
                label="Entities"
                value={result.kpi.entities}
                qualifier="Detected"
                tone="neutral"
                tooltip={
                  <span>
                    Number of distinct entities the analyser detected — people (CEO/CFO/analysts), products, geographies, business segments and named companies. Each is scored individually in the Evidence tab.
                  </span>
                }
              />
              <KpiTile
                label="Sections"
                value={result.kpi.sections}
                qualifier="Parsed"
                tone="neutral"
                tooltip={
                  <span>
                    Number of distinct sections the document was parsed into (prepared remarks, segment commentary, Q&amp;A blocks, etc.). Each section is scored separately in the section-by-section trajectory.
                  </span>
                }
              />
            </div>
            <div className={styles.signalGrid}>
              <SignalCard
                tone="bull"
                title="Key Bull Signals"
                bullets={result.bullSignals}
                tooltip={
                  <span>
                    The three most consequential positive signals from the document, ranked by impact and supported by paragraph references. Used as quick ammunition for a buy thesis or to confirm a constructive view.
                  </span>
                }
              />
              <SignalCard
                tone="bear"
                title="Key Bear Signals"
                bullets={result.bearSignals}
                tooltip={
                  <span>
                    The three most consequential negative signals from the document. Hedged guidance, accelerating subscriber attrition, or non-answers on a key topic — the things that should give a long pause.
                  </span>
                }
              />
            </div>
          </section>

          <section id="briefing" className={styles.section}>
            <AnalyticalBriefing briefing={result.briefing} />
          </section>

          {result.dimensions.length > 0 && (
            <section id="dimensions" className={styles.section}>
              <header className={styles.sectionHeader}>
                <span className={styles.sectionEyebrow}>Sentiment Dimensions</span>
                <h2 className={styles.sectionTitle}>
                  <span className={styles.titleWithInfo}>
                    <span>Five-axis read of the document</span>
                    <InfoPopover
                      label="What are the dimensions?"
                      content={
                        <span>
                          Five axes scored 0–1 that together form the document's "linguistic fingerprint": <strong>Tone &amp; Confidence</strong>, <strong>Forward-Looking Posture</strong>, <strong>Directness</strong> (inverse of hedging), <strong>Q&amp;A Discipline</strong>, and <strong>Quantification</strong>. The radar plots all five at a glance; the bars below give per-dimension qualifiers and supporting evidence.
                        </span>
                      }
                    />
                  </span>
                </h2>
              </header>
              <div className={styles.twoCol}>
                <div className={styles.dimColumn}>
                  {result.dimensions.map((d) => (
                    <DimensionGauge key={d.label} dim={d} tooltip={DIMENSION_TOOLTIPS[d.label]} />
                  ))}
                </div>
                {result.radar.length > 0 && (
                  <div className={styles.radarColumn}>
                    <SentimentRadar axes={result.radar} />
                  </div>
                )}
              </div>
            </section>
          )}

          {result.guidanceBridge && (
            <section id="guidance" className={styles.section}>
              <header className={styles.sectionHeader}>
                <span className={styles.sectionEyebrow}>Guidance Bridge</span>
                <h2 className={styles.sectionTitle}>
                  <span className={styles.titleWithInfo}>
                    <span>Forward outlook reconciled to consensus</span>
                    <InfoPopover
                      label="How is the guidance bridge built?"
                      content={
                        <span>
                          Each piece of forward guidance is extracted, paired with the consensus midpoint (where available), and tagged with the framing management used: <strong>Raised</strong>, <strong>Reaffirmed</strong>, <strong>Lowered</strong>, or <strong>Introduced</strong>. The Δ column shows the implied beat or miss vs consensus midpoint. Net verdict aggregates across items.
                        </span>
                      }
                    />
                  </span>
                </h2>
              </header>
              <GuidanceBridge block={result.guidanceBridge} />
            </section>
          )}
        </div>
      )}

      {(printMode || safeActiveTab === 'evidence') && (
        <div role="tabpanel" id="tabpanel-evidence" aria-labelledby="tab-evidence" className={[styles.tabPanel, printMode ? styles.printPanel : ''].filter(Boolean).join(' ')}>
          {printMode && <h2 className={styles.printTabHeading}>Evidence</h2>}
          {result.entities.length > 0 && (
            <section id="charts" className={styles.section}>
              <header className={styles.sectionHeader}>
                <span className={styles.sectionEyebrow}>Entity Sentiment</span>
                <h2 className={styles.sectionTitle}>Who and what moved the verdict</h2>
              </header>
              <EntityTable rows={result.entities} />
            </section>
          )}

          {result.sections.length > 0 && (
            <section id="sections" className={styles.section}>
              <header className={styles.sectionHeader}>
                <span className={styles.sectionEyebrow}>Section-by-Section</span>
                <h2 className={styles.sectionTitle}>
                  <span className={styles.titleWithInfo}>
                    <span>How the document moved across sections</span>
                    <InfoPopover
                      label="What is the section trajectory?"
                      content={
                        <span>
                          The document is split into logical sections (prepared remarks, segment commentary, Q&amp;A blocks). Each gets its own sentiment score on the −1 to +1 scale. The trajectory shows whether tone strengthens, weakens, or stays stable across the document — useful for spotting where management got cautious.
                        </span>
                      }
                    />
                  </span>
                </h2>
              </header>
              <SectionTimeline sections={result.sections} />
            </section>
          )}

          {result.qna.length > 0 && (
            <section id="qna" className={styles.section}>
              <header className={styles.sectionHeader}>
                <span className={styles.sectionEyebrow}>Q&amp;A Deep Dive</span>
                <h2 className={styles.sectionTitle}>
                  <span className={styles.titleWithInfo}>
                    <span>Analyst questions, executive answers, and what they did and didn’t say</span>
                    <InfoPopover
                      label="What do the Q&A flags mean?"
                      content={
                        <span>
                          Each answer is tagged with one or more flags:{' '}
                          <strong>Confident</strong> (direct, quantified reply), <strong>Hedged</strong> (qualified but transparent), <strong>Deflection</strong> (pivots to an adjacent topic), and <strong>Non-Answer</strong> (fails to address the specific question). Press-level pills on follow-ups (<em>Pressed once / twice / hard</em>) flag where analysts had to push for a real answer.
                        </span>
                      }
                    />
                  </span>
                </h2>
              </header>
              <QnaTable rows={result.qna} resultSlug={result.slug} />
            </section>
          )}

          {result.numerical.length > 0 && (
            <section id="numerical" className={styles.section}>
              <NumericalTable rows={result.numerical} />
            </section>
          )}
        </div>
      )}

      {(printMode || safeActiveTab === 'language') && (
        <div role="tabpanel" id="tabpanel-language" aria-labelledby="tab-language" className={[styles.tabPanel, printMode ? styles.printPanel : ''].filter(Boolean).join(' ')}>
          {printMode && <h2 className={styles.printTabHeading}>Language</h2>}
          {(result.linguisticIndicators.length > 0 || result.fingerprint.length > 0) && (
            <section id="linguistic" className={styles.section}>
              <header className={styles.sectionHeader}>
                <span className={styles.sectionEyebrow}>Linguistic Analysis</span>
                <h2 className={styles.sectionTitle}>
                  <span className={styles.titleWithInfo}>
                    <span>The sentence-level fingerprint</span>
                    <InfoPopover
                      label="What is the linguistic fingerprint?"
                      content={
                        <span>
                          Eight low-level metrics measured directly from the sentence structure: hedge density, forward-modal frequency, active voice ratio, sentence length, lexical density, modal confidence, quantification, and negation. Together they form a fingerprint you can compare against the same speaker's prior calls or the sector baseline. Higher = more of that property; "(inv.)" metrics are inverted so higher always reads as positive.
                        </span>
                      }
                    />
                  </span>
                </h2>
              </header>
              <div className={styles.linguisticGrid}>
                {result.linguisticIndicators.length > 0 && (
                  <ul className={styles.indicatorGrid}>
                    {result.linguisticIndicators.map((ind) => (
                      <li key={ind.label}>
                        <LinguisticIndicatorTile ind={ind} />
                      </li>
                    ))}
                  </ul>
                )}
                {result.fingerprint.length > 0 && (
                  <LinguisticFingerprint metrics={result.fingerprint} />
                )}
              </div>
            </section>
          )}

          {result.sectorLexicon.length > 0 && (
            <section id="lexicon" className={styles.section}>
              <header className={styles.sectionHeader}>
                <span className={styles.sectionEyebrow}>Sector-Specific Lexicon</span>
                <h2 className={styles.sectionTitle}>
                  <span className={styles.titleWithInfo}>
                    <span>Domain vocabulary the analyser flagged</span>
                    <InfoPopover
                      label="How is the sector lexicon scored?"
                      content={
                        <span>
                          The analyser matches sector-specific vocabulary (e.g. <em>installed base</em>, <em>ARPU</em>, <em>NIM</em>, <em>NPL</em>) and tallies polarity for each match. Three counters per sector: <strong>positive</strong>, <strong>negative</strong>, and <strong>risk</strong>. Score is a weighted polarity (positives minus negatives, with risk-category matches at 0.5× weight) on the −1 to +1 scale.
                        </span>
                      }
                    />
                  </span>
                </h2>
              </header>
              <SectorLexiconAccordion sectors={result.sectorLexicon} />
            </section>
          )}

          {result.tailRisk && (
            <section id="tailrisk" className={styles.section}>
              <header className={styles.sectionHeader}>
                <span className={styles.sectionEyebrow}>Tail-Risk Quantification</span>
                <h2 className={styles.sectionTitle}>Calibrated read on hedge-language probability</h2>
              </header>
              <TailRiskMeter block={result.tailRisk} />
            </section>
          )}
        </div>
      )}

      {(printMode || safeActiveTab === 'context') && (
        <div role="tabpanel" id="tabpanel-context" aria-labelledby="tab-context" className={[styles.tabPanel, printMode ? styles.printPanel : ''].filter(Boolean).join(' ')}>
          {printMode && <h2 className={styles.printTabHeading}>Context</h2>}
          {result.priorQuarter && (
            <section id="qoq" className={styles.section}>
              <header className={styles.sectionHeader}>
                <span className={styles.sectionEyebrow}>Vs. Prior Quarter</span>
                <h2 className={styles.sectionTitle}>
                  <span className={styles.titleWithInfo}>
                    <span>What changed</span>
                    <InfoPopover
                      label="How is the prior-quarter diff calculated?"
                      content={
                        <span>
                          The prior call was scored on the same five dimensions and aggregate verdict. Each Δ shows current minus prior: positive = improvement, negative = deterioration. Useful for spotting tone drift even when the absolute scores look similar.
                        </span>
                      }
                    />
                  </span>
                </h2>
              </header>
              <PriorQuarterDiffPanel
                current={{
                  verdict: result.verdict,
                  score: result.score,
                  confidence: result.confidence,
                  dimensions: result.dimensions,
                  date: result.date,
                  title: result.title,
                }}
                prior={result.priorQuarter}
              />
            </section>
          )}

          {result.cohort && (
            <section id="peers" className={styles.section}>
              <header className={styles.sectionHeader}>
                <span className={styles.sectionEyebrow}>Vs. Peers</span>
                <h2 className={styles.sectionTitle}>
                  <span className={styles.titleWithInfo}>
                    <span>Where this document lands in its cohort</span>
                    <InfoPopover
                      label="What is the peer cohort?"
                      content={
                        <span>
                          A roll-up of similar documents (same sector, same period) gives a baseline distribution per dimension. The bar shows the cohort's P10–P90 range, the tick is the mean, and the marker is where this document falls. <strong>P-rank</strong> = the percentile this document scored against the cohort.
                        </span>
                      }
                    />
                  </span>
                </h2>
              </header>
              <CohortPercentilePanel cohort={result.cohort} />
            </section>
          )}

          {result.speakers && result.speakers.length > 0 && (
            <section id="speakers" className={styles.section}>
              <header className={styles.sectionHeader}>
                <span className={styles.sectionEyebrow}>Speaker Attribution</span>
                <h2 className={styles.sectionTitle}>
                  <span className={styles.titleWithInfo}>
                    <span>Sentiment broken out per executive</span>
                    <InfoPopover
                      label="What is speaker attribution?"
                      content={
                        <span>
                          Each utterance is tagged with its speaker (CEO, CFO, IR, analyst). Per-speaker scores reveal cases where, for example, the CEO sounds confident while the CFO hedges — a signal that's invisible if you score the document as a single voice. <strong>Hedge density</strong> = % of clauses with hedging modal verbs; <strong>forward modals</strong> = frequency of "expect / anticipate / see"-style verbs.
                        </span>
                      }
                    />
                  </span>
                </h2>
              </header>
              <SpeakerSentimentPanel speakers={result.speakers} />
            </section>
          )}

          {result.riskQuotes.length > 0 && (
            <section id="risk" className={styles.section}>
              <header className={styles.sectionHeader}>
                <span className={styles.sectionEyebrow}>Risk &amp; Quotes</span>
                <h2 className={styles.sectionTitle}>
                  <span className={styles.titleWithInfo}>
                    <span>The hedged, the cautioned, and the litigated</span>
                    <InfoPopover
                      label="How are risk quotes categorized?"
                      content={
                        <span>
                          Quotes flagged for hedging, cautious framing, or explicit risk language. Each is tagged with its topical context: <em>guidance</em>, <em>demand</em>, <em>cost</em>, <em>competitive</em>, <em>macro</em>, or <em>litigation</em>. These are the lines an analyst should re-read in the source document before forming a view.
                        </span>
                      }
                    />
                  </span>
                </h2>
              </header>
              <RiskQuotes quotes={result.riskQuotes} />
            </section>
          )}

          {result.verification.length > 0 && (
            <section id="verification" className={styles.section}>
              <header className={styles.sectionHeader}>
                <span className={styles.sectionEyebrow}>Verification</span>
                <h2 className={styles.sectionTitle}>
                  <span className={styles.titleWithInfo}>
                    <span>Quality checks the analyser ran on this document</span>
                    <InfoPopover
                      label="What do verification checks mean?"
                      content={
                        <span>
                          Self-checks the analyser runs on its own output: do quantified claims match their qualitative framing, are forward-looking statements properly flagged, were Q&amp;A non-answers detected, etc. Statuses: <strong>pass</strong> (clean), <strong>flag</strong> (worth a human eyeball), <strong>fail</strong> (the analysis is unreliable for that check). Helps you trust the rest of the result.
                        </span>
                      }
                    />
                  </span>
                </h2>
              </header>
              <VerificationList items={result.verification} />
            </section>
          )}

          {result.summary && (
            <section id="summary" className={styles.section}>
              <header className={styles.sectionHeader}>
                <span className={styles.sectionEyebrow}>One-Line Summary</span>
                <h2 className={styles.sectionTitle}>{result.summary}</h2>
              </header>
            </section>
          )}

          {result.advanced.length > 0 && (
            <section id="advanced" className={styles.section}>
              <header className={styles.sectionHeader}>
                <span className={styles.sectionEyebrow}>Advanced</span>
                <h2 className={styles.sectionTitle}>Run details &amp; debug</h2>
              </header>
              <button
                type="button"
                className={styles.advancedToggle}
                onClick={() => setAdvancedOpen((o) => !o)}
                aria-expanded={advancedOpen}
              >
                {advancedOpen ? 'Hide' : 'Show'} advanced details
              </button>
              {(advancedOpen || printMode) && (
                <dl className={styles.advancedList}>
                  {result.advanced.map((kv) => (
                    <div key={kv.key} className={styles.advancedRow}>
                      <dt>{kv.key}</dt>
                      <dd className="mono">{kv.value}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </section>
          )}
        </div>
      )}

      <ShortcutsModal open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
    </ScreenShell>
  );
}
